/**
 * Секция «Настройка чувствительности детектора падений» (/admin/fall)
 */

import React, { FC, useContext, useEffect } from 'react';
import { connect } from 'react-redux';
import { Button, Card, Form, InputNumber, Slider, Space, Switch, Typography } from 'antd';
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons';

import { Dispatch, iRootState } from '../../../store';
import { useAsync } from '../../../hooks';
import { IFallSettings } from '../../../models/types';

import Spinner from '../../../components_/Spinner';
import EmptyState from '../../../components_/EmptyState';
import { ToastContext } from '../../../components_/Toast';

import {
  describeSaveFailure,
  isFallSettings,
  isPartialSave,
  toFallFormValues,
  IMPACT_SPIKE_DISABLED,
  IMPACT_SPIKE_MIN,
  IMPACT_SPIKE_MAX,
  IFallFormValues
} from './utils';

const THRESHOLD_MIN = 0;
const THRESHOLD_MAX = 1;
const THRESHOLD_STEP = 0.1;

const mapState = (state: iRootState) => ({
  data: state.settingServices.data,
  loading: state.settingServices.loading
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    getFallSettings: d.settingServices.getFallSettings,
    saveFallSettings: d.settingServices.saveFallSettings,
    setData: d.settingServices.setData,
    reset: d.settingServices.reset
  };
};

type ConnectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const FallSection: FC<ConnectedProps> = ({
  data,
  loading,
  getFallSettings,
  saveFallSettings,
  setData,
  reset
}) => {
  const [form] = Form.useForm();
  const toast = useContext(ToastContext);

  const [runGetFallSettings, isLoadingGetFallSettings] = useAsync(getFallSettings);
  const [runSaveFallSettings, isLoadingSaveFallSettings] = useAsync(saveFallSettings);

  useEffect(() => {
    runGetFallSettings();

    return () => {
      // reset общий для вкладки: секции монтируются/размонтируются вместе
      reset();
    };
  }, []);

  useEffect(() => {
    if (data) {
      form.setFieldsValue(toFallFormValues(data));
    }
  }, [data, form]);

  const handleReload = () => {
    runGetFallSettings();
  };

  const handleSave = async () => {
    let values: IFallFormValues;

    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    // Страхуемся от артефактов float-арифметики слайдера (0.30000000000000004).
    // PUT требует все три поля — иначе BFF/INS отвечает 400.
    // Выключенная детекция удара хранится как 0
    const payload: IFallSettings = {
      threshold: Math.round(values.threshold * 10) / 10,
      dedupWindowSeconds: values.dedupWindowSeconds,
      impactSpikeThreshold: values.impactEnabled
        ? Math.round(values.impactSpikeThreshold * 10) / 10
        : IMPACT_SPIKE_DISABLED
    };

    // Эффект возвращает либо ответ (2xx), либо ошибку axios со статусом в response.
    // Внимание: успешный ответ тоже truthy, поэтому флаг isError из useAsync
    // здесь не используется — статус и тело читаем из самого результата
    const result: any = await runSaveFallSettings(payload);
    const response = result?.isAxiosError ? result.response : result;
    const status: number | undefined = response?.status;
    const contentType = String(response?.headers?.['content-type'] ?? '');

    if (status === 200 || status === 202) {
      // PUT возвращает фактически применённую тройку — она может отличаться от отправленной
      if (isFallSettings(response.data)) {
        form.setFieldsValue(toFallFormValues(response.data));
        // Держим модель в актуальном состоянии на случай будущих читателей
        setData(response.data);
      }
      toast({
        text:
          status === 202
            ? 'Настройки применены, но пока не сохранены постоянно'
            : 'Настройки сохранены',
        type: 'success'
      });
      return;
    }

    if (status === 503 && isPartialSave(contentType)) {
      // Частичное сохранение: восстанавливаем в форме прежние значения из тела ответа
      if (isFallSettings(response.data)) {
        form.setFieldsValue(toFallFormValues(response.data));
        setData(response.data);
      }
      toast({
        text: 'Сохранить не удалось — восстановлены предыдущие значения',
        type: 'error'
      });
      return;
    }

    toast({ text: describeSaveFailure(status, response?.data), type: 'error' });
  };

  const isLoading = loading || isLoadingGetFallSettings || isLoadingSaveFallSettings;

  const renderBody = () => {
    if (isLoading) {
      return <Spinner />;
    }

    if (!data) {
      return <EmptyState variant="serverError" />;
    }

    return (
      <Form form={form} layout="vertical" style={{ maxWidth: 480 }}>
        <Form.Item
          name="threshold"
          label="Порог срабатывания (threshold)"
          rules={[{ required: true, message: 'Обязательное поле' }]}
        >
          <Slider
            min={THRESHOLD_MIN}
            max={THRESHOLD_MAX}
            step={THRESHOLD_STEP}
            marks={{ 0: '0', 0.5: '0.5', 1: '1' }}
          />
        </Form.Item>
        <Form.Item
          name="dedupWindowSeconds"
          label="Окно дедупликации, сек (dedupWindowSeconds)"
          rules={[
            { required: true, message: 'Обязательное поле' },
            {
              validator: async (_: any, value: number) => {
                if (!Number.isInteger(value) || value < 0) {
                  throw new Error('Укажите целое число секунд (0 и более)');
                }
              }
            }
          ]}
        >
          <InputNumber min={0} precision={0} step={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="impactEnabled"
          valuePropName="checked"
          label="Детекция удара (impactSpikeThreshold)"
          extra="Выключено (0): событие только по свободному падению. Включено: дополнительно по вектору ускорения ≥ порога; 1.1 — максимальная чувствительность, 3 — минимальная"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prev: any, cur: any) => prev.impactEnabled !== cur.impactEnabled}
        >
          {({ getFieldValue }: any) =>
            getFieldValue('impactEnabled') ? (
              <Form.Item
                name="impactSpikeThreshold"
                label="Порог удара"
                rules={[{ required: true, message: 'Обязательное поле' }]}
              >
                <Slider
                  min={IMPACT_SPIKE_MIN}
                  max={IMPACT_SPIKE_MAX}
                  step={0.1}
                  marks={{ 1.1: '1.1', 2: '2', 3: '3' }}
                />
              </Form.Item>
            ) : null
          }
        </Form.Item>
      </Form>
    );
  };

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Настройка чувствительности детектора падений
          </Typography.Title>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleReload} disabled={isLoading}>
              Обновить
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              disabled={isLoading || !data}
            >
              Сохранить
            </Button>
          </Space>
        </Space>
        {renderBody()}
      </Space>
    </Card>
  );
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(FallSection);
